import { Button } from '@invent/wl-ui-kit-next/ui';
import { ArrowRight, MailX, Paperclip } from 'lucide-react';
import * as React from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import {
  CompactBar,
  CompactChipLine,
  CompactDivider,
  CompactFilters,
  CompactTitle,
  DataTable,
  HelpPopover,
  NotPermitted,
  SelectChip,
  StatusChip,
  PageHeader,
  STICKY_TABLE_TOP,
  useScrolledPast,
  useWorkToolbar,
  WorkToolbar,
  type DataTableState,
  type ICompactChip,
  type IDataColumn,
  type IWorkToolbar
} from '@/design';
import { useCan } from '@/domains/auth';
import { formatDateTime } from '@/domains/survey';
import type { IMailCampaignSummary } from '../../api/mail-campaign-contracts';
import { mailCampaignsApi } from '../../api/mail-campaigns-api';
import type { IMailLogRow, IMailReply, IMailSuppression } from '../../api/mail-log-contracts';
import { mailLogApi } from '../../api/mail-log-api';
import type { IPage } from '../../api/oversight-contracts';
import { LoadError, PastTheEnd, SectionTabs, useAdminData, type IAdminTab } from '../../components/admin-page';
import { personHubPath } from '../../components/participant-link';
import { countOf } from '../../lists';
import { TEAM_PATHS } from '../../paths';
import { refusalMessage } from '../../refusal';
import { CompactTabs, PageAction, useKnownFunnels, type IKnownFilter } from '../lists/work-page/light-list';
import { WorkPageHeaderActions, useReadAt } from '../lists/work-page/header-actions';
import { WorkPager, dropPaging, readItemPages, useWorkPaging } from '../lists/work-page/pager';
import { RecordSearch } from '../record-lists/record-work-page';
import { ParamChip } from '../sec-adv/firm-filters';
import { TagChips, useMailTags } from '../mail-templates/template-tags';
import { CampaignSheet } from './campaign-sheet';
import { DateChip, dayStart, MultiChip, rangeWords, useTemplateChoices, type IMultiOption } from './log-filters';
import {
  CAMPAIGN_KINDS,
  CAMPAIGN_STATUSES,
  CampaignStatusChip,
  DeliveryChip,
  kindLabel,
  MAIL_DELIVERY,
  MAIL_KINDS,
  MAIL_STATUSES,
  MailStatusChip,
  sourceLabel,
  SUPPRESSION_SOURCES,
  variantWords,
  vocabulary
} from './mail-words';
import { ClearSuppressionDialog, ReplyDrawer, SuppressDialog } from './suppression-dialogs';

/**
 * Setup → Email log (SR-523 stage 3): every mail the worker handled — **Sends**, a row of
 * `notification_log` each, newest first — the bulk **Campaigns**, a campaign's detail beside them
 * (`?campaign=`), and the **Suppressions** bulk mail skips. A light work page (the work pages
 * design, "Three forms"): header with its tabs, the key number, *Refresh* and the "?"; the search;
 * the columns' funnels over known vocabularies; the filter line; the table; the pager. No cards,
 * no counts read.
 *
 * Reading it needs `responses.browse` beside `mail.templates` or `mail.outreach.send` (survey-api's
 * `mail.log`): its rows are people's addresses. The Suppressions tab and every change there are
 * `mail.outreach.send`'s; clearing one takes a note, since it means the person consented again.
 * Replies no mail matched wait in **Unmatched replies**, read and marked reviewed with a note; nothing
 * else is done with them.
 */

const LOG_MAX_PAGE = 200;

export function useMailLogTabs(): IAdminTab[] {
  const can = useCan();
  return [
    { to: TEAM_PATHS.mailLog, label: 'Sends', end: true },
    { to: `${TEAM_PATHS.mailLog}/campaigns`, label: 'Campaigns' },
    { to: `${TEAM_PATHS.mailLog}/replies`, label: 'Unmatched replies' },
    ...(can('mail.outreach.send') ? [{ to: `${TEAM_PATHS.mailLog}/suppressions`, label: 'Suppressions' }] : [])
  ];
}

/** The log's guard: whoever edits templates or sends outreach, and reads responses. */
function useCanReadLog(): boolean {
  const can = useCan();
  return (can('mail.templates') || can('mail.outreach.send')) && can('responses.browse');
}

/** One paged read of a tab, the last answer kept while the next is out. */
function usePagedRead<T>(
  asked: string,
  read: (page: number, pageSize: number, signal: AbortSignal) => Promise<IPage<T>>,
  fallback: string
) {
  const paging = useWorkPaging();
  const { page, perPage, pages } = paging;
  const key = JSON.stringify([asked, page, perPage, pages]);
  const { data, error, reading, reload } = useAdminData(
    async (signal) => ({
      key,
      page: await readItemPages(
        async (at, size) => {
          try {
            return await read(at, size, signal);
          } catch (failure) {
            throw new Error(refusalMessage(failure, fallback));
          }
        },
        page,
        perPage,
        pages,
        LOG_MAX_PAGE
      )
    }),
    [key]
  );
  const failed = error !== null && !reading;
  const shown = failed && data?.key !== key ? null : data;
  const readAt = useReadAt(data);
  const tableState = (counted: [string, string], empty: string): DataTableState | undefined =>
    shown === null
      ? failed
        ? { kind: 'error', content: <LoadError message={error} /> }
        : { kind: 'loading' }
      : shown.page.items.length > 0
        ? undefined
        : {
            kind: 'empty',
            content:
              shown.page.total > 0 ? (
                <PastTheEnd
                  page={shown.page.page}
                  pageSize={shown.page.pageSize}
                  total={shown.page.total}
                  counted={counted}
                  onPage={paging.setPage}
                />
              ) : (
                empty
              )
          };
  return {
    paging,
    shown,
    failed,
    error,
    reading,
    reload,
    total: shown?.key === key ? shown.page.total : null,
    refresh: { reading, readAt, onClick: () => void reload() },
    tableState
  };
}

/** The address's filters, and their writers: a change starts at the top. */
function useFilters(cleared: readonly string[]) {
  const [params, setParams] = useSearchParams();
  const update = (mutate: (next: URLSearchParams) => void, replace = false) =>
    setParams(
      (current) => {
        const next = dropPaging(new URLSearchParams(current));
        mutate(next);
        return next;
      },
      { replace }
    );
  const q = params.get('q') ?? '';
  return {
    params,
    setParams,
    q,
    search: (text: string, reload: () => void) => {
      if (text === q) reload();
      else update((next) => (text === '' ? next.delete('q') : next.set('q', text)), true);
    },
    choose: (name: string, value: Nullable<string>) =>
      update((next) => (value === null ? next.delete(name) : next.set(name, value))),
    /** A filter of several values, as repeated parameters. */
    chooseAll: (name: string, values: readonly string[]) =>
      update((next) => {
        next.delete(name);
        for (const value of values) next.append(name, value);
      }),
    /** Several filters at once — a date range's two ends. */
    chooseEach: (values: Record<string, Nullable<string>>) =>
      update((next) => {
        for (const [name, value] of Object.entries(values)) {
          if (value === null) next.delete(name);
          else next.set(name, value);
        }
      }),
    clearAll: () =>
      update((next) => {
        for (const name of cleared) next.delete(name);
      })
  };
}

/** The header, compact bar, toolbar and filter line every tab shares. */
function LogFrame({
  summary,
  refresh,
  help,
  search,
  end,
  chips,
  compactChips = chips,
  quick = null,
  quickCount = 0,
  toolbar: given,
  compactOpen,
  onCompactOpen,
  onClearAll,
  reading,
  found,
  children
}: {
  summary: Nullable<string>;
  refresh: React.ComponentProps<typeof WorkPageHeaderActions>['refresh'];
  help: React.ReactNode;
  search: (compact: boolean) => React.ReactNode;
  end?: React.ReactNode;
  chips: ICompactChip[];
  /** The compact bar's chips — a press opens its *Filters*; the page's by default. */
  compactChips?: ICompactChip[];
  /** The toolbar's quick filters, drawn again in the compact bar's *Filters*. */
  quick?: React.ReactNode;
  quickCount?: number;
  /** The page's, when its chips open a quick filter. */
  toolbar?: IWorkToolbar;
  compactOpen?: boolean;
  onCompactOpen?: (open: boolean) => void;
  onClearAll: () => void;
  reading: boolean;
  found: Nullable<number>;
  children: React.ReactNode;
}) {
  const tabs = useMailLogTabs();
  const own = useWorkToolbar();
  const toolbar = given ?? own;
  const [controlsEnd, setControlsEnd] = React.useState<Nullable<HTMLElement>>(null);
  const scrolled = useScrolledPast(controlsEnd);
  return (
    <>
      <PageHeader
        title="Email log"
        row
        tabs={<SectionTabs tabs={tabs} />}
        actions={<WorkPageHeaderActions summary={summary} refresh={refresh} help={help} />}
      />
      <CompactBar
        shown={scrolled}
        label="Email log, compact"
        actions={<WorkPageHeaderActions summary={null} refresh={refresh} help={help} />}
        below={compactChips.length > 0 && <CompactChipLine chips={compactChips} onClearAll={onClearAll} />}
      >
        <CompactTitle>Email log</CompactTitle>
        <CompactTabs label="Section" tabs={tabs} />
        <CompactDivider />
        {search(true)}
        {quick && (
          <CompactFilters count={quickCount} open={compactOpen} onOpenChange={onCompactOpen}>
            {quick}
          </CompactFilters>
        )}
        <span className="flex-1" />
        {end && (
          <>
            <CompactDivider />
            {end}
          </>
        )}
      </CompactBar>
      <div className="grid min-w-0 gap-3 px-6 py-4">
        <WorkToolbar
          toolbar={toolbar}
          status={{ reading, count: found === null ? undefined : `${found.toLocaleString('en-US')} found` }}
          search={search(false)}
          quick={quick}
          quickCount={quickCount}
          end={end}
        />
        {chips.length > 0 && <CompactChipLine label="Filters" chips={chips} onClearAll={onClearAll} />}
        <div ref={setControlsEnd} aria-hidden data-controls-end="" className="-mt-3 h-0" />
        {children}
      </div>
    </>
  );
}

function Denied({ what }: { what: string }) {
  return (
    <>
      <PageHeader title="Email log" />
      <div className="px-6 py-6">
        <NotPermitted what={what} />
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------------------------
// Sends
// ---------------------------------------------------------------------------------------------

/** One value of a vocabulary, a toolbar chip each. */
interface ISelectFilter {
  param: string;
  label: string;
  any: string;
  options: readonly { value: string; label: string }[];
  labelOf: (value: string) => string;
}

const SEND_SELECTS: readonly ISelectFilter[] = [
  { param: 'kind', label: 'Kind', any: 'Any kind', options: vocabulary(MAIL_KINDS), labelOf: kindLabel },
  {
    param: 'status',
    label: 'Status',
    any: 'Any status',
    options: vocabulary(MAIL_STATUSES),
    labelOf: (value) => MAIL_STATUSES[value]?.label ?? value
  },
  {
    param: 'delivery',
    label: 'Delivery',
    any: 'Any delivery',
    options: vocabulary(MAIL_DELIVERY),
    labelOf: (value) => MAIL_DELIVERY[value]?.label ?? value
  }
];

/** Filters a link sets (a template's Delivery, a campaign) and the toolbar has no chip for. */
const FIXED_FILTERS: readonly { param: string; words: (value: string) => string }[] = [
  { param: 'survey', words: (value) => `Survey: ${value}` },
  { param: 'version', words: (value) => `Version ${value}` },
  { param: 'campaign', words: () => 'One campaign' }
];

const SEND_CLEARED = [
  'q',
  'template',
  'tag',
  'from',
  'to',
  ...SEND_SELECTS.map((filter) => filter.param),
  ...FIXED_FILTERS.map((filter) => filter.param)
];

/** The tags offered: presets and every tag in use. */
function tagChoices(tags: ReturnType<typeof useMailTags>): IMultiOption[] {
  return tags.map((entry) => ({ value: entry.tag, label: entry.tag }));
}

/** The toolbar's Template and Tag chips, and the applied filters' chips for them — the Sends'
 *  and the Campaigns' alike. */
function useTemplateFilters(filters: ReturnType<typeof useFilters>) {
  const { params } = filters;
  const templates = params.getAll('template');
  const tags = params.getAll('tag');
  const templateOptions = useTemplateChoices();
  const tagOptions = tagChoices(useMailTags());
  const nameOf = (code: string) => templateOptions.find((option) => option.value === code)?.label ?? code;
  const chips = (open: (label: string) => true): ICompactChip[] => [
    ...(templates.length > 0
      ? [
          {
            key: 'template',
            chip: (
              <ParamChip
                words={`Template: ${templates.map(nameOf).join(', ')}`}
                body={null}
                onPress={() => open('Template')}
                onRemove={() => filters.chooseAll('template', [])}
              />
            )
          }
        ]
      : []),
    ...(tags.length > 0
      ? [
          {
            key: 'tag',
            chip: (
              <ParamChip
                words={`Tag: ${tags.join(', ')}`}
                body={null}
                onPress={() => open('Tag')}
                onRemove={() => filters.chooseAll('tag', [])}
              />
            )
          }
        ]
      : [])
  ];
  const quick = (
    <>
      <MultiChip
        label="Template"
        values={templates}
        options={templateOptions}
        onChange={(next) => filters.chooseAll('template', next)}
        empty="No template to choose from."
      />
      <MultiChip
        label="Tag"
        values={tags}
        options={tagOptions}
        onChange={(next) => filters.chooseAll('tag', next)}
        empty="No tag is in use."
      />
    </>
  );
  return { templates, tags, chips, quick };
}

/** A toolbar's state and its compact bar's *Filters*, opened by an applied filter's chip. */
function useQuick() {
  const toolbar = useWorkToolbar();
  const [compactOpen, setCompactOpen] = React.useState(false);
  const openCompact = () => {
    setCompactOpen(true);
    return true as const;
  };
  return { toolbar, compactOpen, setCompactOpen, openCompact };
}

export function AdminMailLogPage() {
  if (!useCanReadLog()) return <Denied what="reading the mail log" />;
  return <Sends />;
}

function Sends() {
  const filters = useFilters(SEND_CLEARED);
  const { params, q } = filters;
  const byTemplate = useTemplateFilters(filters);
  const quickState = useQuick();
  const from = params.get('from');
  const to = params.get('to');
  const query = {
    q: q || undefined,
    kind: params.get('kind') || undefined,
    status: params.get('status') || undefined,
    delivery: params.get('delivery') || undefined,
    template: byTemplate.templates,
    tag: byTemplate.tags,
    survey: params.get('survey') || undefined,
    version: params.get('version') ? Number(params.get('version')) : undefined,
    campaign: params.get('campaign') || undefined,
    from: dayStart(from),
    to: dayStart(to, true)
  };
  const read = usePagedRead<IMailLogRow>(
    JSON.stringify(query),
    (page, pageSize, signal) => mailLogApi.log({ ...query, page, pageSize }, signal),
    'The mail log could not be read'
  );
  const range = rangeWords(from, to);
  const chipsWith = (open: (label: string) => true): ICompactChip[] => [
    ...FIXED_FILTERS.flatMap(({ param, words }) => {
      const value = params.get(param);
      if (!value) return [];
      return [
        {
          key: param,
          chip: (
            <ParamChip
              words={words(value)}
              body={<p className="px-2 py-1 text-xs text-muted-foreground">Set by the link that opened the log.</p>}
              onPress={() => false}
              onRemove={() => filters.choose(param, null)}
            />
          )
        }
      ];
    }),
    ...byTemplate.chips(open),
    ...SEND_SELECTS.flatMap((filter) => {
      const value = params.get(filter.param);
      if (!value) return [];
      return [
        {
          key: filter.param,
          chip: (
            <ParamChip
              words={`${filter.label}: ${filter.labelOf(value)}`}
              body={null}
              onPress={() => open(filter.label)}
              onRemove={() => filters.choose(filter.param, null)}
            />
          )
        }
      ];
    }),
    ...(range
      ? [
          {
            key: 'date',
            chip: (
              <ParamChip
                words={`Date: ${range}`}
                body={null}
                onPress={() => open('Date')}
                onRemove={() => filters.chooseEach({ from: null, to: null })}
              />
            )
          }
        ]
      : [])
  ];
  const chips = chipsWith(quickState.toolbar.openQuick);
  const filtered = chips.length > 0;
  const quick = (
    <>
      {byTemplate.quick}
      {SEND_SELECTS.map((filter) => (
        <SelectChip
          key={filter.param}
          label={filter.label}
          any={filter.any}
          value={params.get(filter.param) || null}
          options={filter.options}
          onChoose={(value) => filters.choose(filter.param, value)}
        />
      ))}
      <DateChip from={from} to={to} onChange={(nextFrom, nextTo) => filters.chooseEach({ from: nextFrom, to: nextTo })} />
    </>
  );
  const help = (
    <HelpPopover label="About the Email log" align="end">
      <p>
        Every email the system handled, newest first: sent, failed, or skipped — unsubscribed, no
        address, or withheld on a test environment. Delivery is what the provider reported after
        the send.
      </p>
      <p>The search finds a recipient&apos;s address or a participant&apos;s name.</p>
      <p>
        Several templates or tags find the emails of any of them; the filters together narrow each
        other. A tag is the template&apos;s: tags only label mail.
      </p>
    </HelpPopover>
  );

  return (
    <LogFrame
      summary={read.total === null ? null : countOf(read.total, 'email')}
      refresh={read.refresh}
      help={help}
      search={(compact) => (
        <RecordSearch
          q={q}
          label="Filter emails"
          placeholder="Address or name…"
          onSearch={(text) => filters.search(text, () => void read.reload())}
          compact={compact}
        />
      )}
      chips={chips}
      compactChips={chipsWith(quickState.openCompact)}
      quick={quick}
      quickCount={chips.length}
      toolbar={quickState.toolbar}
      compactOpen={quickState.compactOpen}
      onCompactOpen={quickState.setCompactOpen}
      onClearAll={filters.clearAll}
      reading={read.reading}
      found={read.shown?.page.total ?? null}
    >
      {read.failed && read.shown !== null && (
        <LoadError message={`Could not refresh — showing the emails as last read. ${read.error}`} />
      )}
      <DataTable
        label="Emails"
        columns={SEND_COLUMNS}
        rows={read.tableState(['email', 'emails'], '') ? null : (read.shown?.page.items ?? null)}
        rowKey={(row) => String(row.id)}
        rowProps={(row) => ({ 'data-mail-log': row.id }) as React.HTMLAttributes<HTMLTableRowElement>}
        busy={read.reading && read.shown !== null}
        state={read.tableState(['email', 'emails'], filtered || q ? 'No email matches.' : 'No email sent yet.')}
        minWidth="72rem"
        stickyTop={STICKY_TABLE_TOP}
      />
      {read.shown && <WorkPager {...read.paging.pager} total={read.shown.page.total} busy={read.reading} sticky />}
    </LogFrame>
  );
}

/** A mail's or a campaign's template: its name, the variant and version under it, its tags. */
function TemplateCell({
  name,
  survey,
  version,
  tags
}: {
  name: string;
  survey: Nullable<string>;
  version: Nullable<number>;
  tags: readonly string[];
}) {
  return (
    <span className="grid justify-items-start gap-0.5">
      <span>{name}</span>
      <span className="text-xs text-muted-foreground">{variantWords(survey, version)}</span>
      <TagChips tags={tags} max={2} />
    </span>
  );
}

const SEND_COLUMNS: IDataColumn<IMailLogRow>[] = [
  {
    id: 'at',
    header: 'When',
    className: 'whitespace-nowrap text-xs text-muted-foreground',
    cell: (row) => formatDateTime(row.sentAt ?? row.at)
  },
  {
    id: 'recipient',
    header: 'Recipient',
    className: 'break-all',
    cell: (row) => (
      <span className="grid">
        <span>{row.recipient}</span>
        {row.cc.length > 0 && <span className="text-xs text-muted-foreground">CC {row.cc.join(', ')}</span>}
      </span>
    )
  },
  {
    id: 'participant',
    header: 'Participant',
    cell: (row) =>
      row.participant ? (
        <Link to={personHubPath(row.participant.id)} className="text-[var(--color-sapphire)] hover:underline">
          {row.participant.name}
        </Link>
      ) : (
        <span className="text-muted-foreground">—</span>
      )
  },
  {
    id: 'template',
    header: 'Template',
    cell: (row) => (
      <TemplateCell
        name={row.templateName ?? row.template ?? '—'}
        survey={row.survey}
        version={row.version}
        tags={row.tags}
      />
    )
  },
  { id: 'kind', header: 'Kind', cell: (row) => kindLabel(row.kind) },
  { id: 'status', header: 'Status', cell: (row) => <MailStatusChip status={row.status} reason={row.reason} /> },
  {
    id: 'delivery',
    header: 'Delivery',
    cell: (row) => (
      <span className="grid justify-items-start gap-0.5">
        <DeliveryChip delivery={row.delivery} />
        {row.deliveryAt && <span className="text-xs text-muted-foreground">{formatDateTime(row.deliveryAt)}</span>}
      </span>
    )
  },
  {
    id: 'replies',
    header: 'Replies',
    numeric: true,
    cell: (row) =>
      row.replies > 0 && row.participant ? (
        <Link
          to={`${personHubPath(row.participant.id)}#activity`}
          className="text-[var(--color-sapphire)] hover:underline"
          title="The replies are on the record's activity"
          onClick={(event) => event.stopPropagation()}
        >
          {row.replies}
        </Link>
      ) : row.replies > 0 ? (
        row.replies
      ) : (
        <span className="text-muted-foreground">—</span>
      )
  }
];

// ---------------------------------------------------------------------------------------------
// Campaigns
// ---------------------------------------------------------------------------------------------

const CAMPAIGN_SELECTS: readonly ISelectFilter[] = [
  {
    param: 'kind',
    label: 'Kind',
    any: 'Any kind',
    options: vocabulary(CAMPAIGN_KINDS),
    labelOf: (value) => CAMPAIGN_KINDS[value] ?? value
  },
  {
    param: 'status',
    label: 'Status',
    any: 'Any status',
    options: vocabulary(CAMPAIGN_STATUSES),
    labelOf: (value) => CAMPAIGN_STATUSES[value]?.label ?? value
  }
];

export function AdminMailCampaignsPage() {
  if (!useCanReadLog()) return <Denied what="reading the mail log" />;
  return <Campaigns />;
}

function Campaigns() {
  const filters = useFilters(['kind', 'status', 'template', 'tag']);
  const { params, setParams } = filters;
  const byTemplate = useTemplateFilters(filters);
  const quickState = useQuick();
  const query = {
    kind: params.get('kind') || undefined,
    status: params.get('status') || undefined,
    template: byTemplate.templates,
    tag: byTemplate.tags
  };
  const read = usePagedRead<IMailCampaignSummary>(
    JSON.stringify(query),
    (page, pageSize, signal) =>
      mailCampaignsApi.list({ ...query, kind: query.kind as never, status: query.status as never, page, pageSize }, signal),
    'The campaigns could not be read'
  );
  const chipsWith = (open: (label: string) => true): ICompactChip[] => [
    ...byTemplate.chips(open),
    ...CAMPAIGN_SELECTS.flatMap((filter) => {
      const value = params.get(filter.param);
      if (!value) return [];
      return [
        {
          key: filter.param,
          chip: (
            <ParamChip
              words={`${filter.label}: ${filter.labelOf(value)}`}
              body={null}
              onPress={() => open(filter.label)}
              onRemove={() => filters.choose(filter.param, null)}
            />
          )
        }
      ];
    })
  ];
  const chips = chipsWith(quickState.toolbar.openQuick);
  const quick = (
    <>
      {byTemplate.quick}
      {CAMPAIGN_SELECTS.map((filter) => (
        <SelectChip
          key={filter.param}
          label={filter.label}
          any={filter.any}
          value={params.get(filter.param) || null}
          options={filter.options}
          onChoose={(value) => filters.choose(filter.param, value)}
        />
      ))}
    </>
  );
  const open = params.get('campaign');
  const setOpen = (id: Nullable<string>) =>
    setParams(
      (now) => {
        const next = new URLSearchParams(now);
        if (id) next.set('campaign', id);
        else next.delete('campaign');
        return next;
      },
      { replace: true }
    );
  const help = (
    <HelpPopover label="About campaigns" align="end">
      <p>
        One send each: an outreach email to a Lists selection, or a cycle&apos;s invitation or
        reminder as it fired. A campaign&apos;s emails go out within the daily sending limits; the
        ones not yet out can be cancelled.
      </p>
      <p>Its tags are its template&apos;s, as they are now.</p>
    </HelpPopover>
  );
  return (
    <LogFrame
      summary={read.total === null ? null : countOf(read.total, 'campaign')}
      refresh={read.refresh}
      help={help}
      search={() => null}
      chips={chips}
      compactChips={chipsWith(quickState.openCompact)}
      quick={quick}
      quickCount={chips.length}
      toolbar={quickState.toolbar}
      compactOpen={quickState.compactOpen}
      onCompactOpen={quickState.setCompactOpen}
      onClearAll={filters.clearAll}
      reading={read.reading}
      found={read.shown?.page.total ?? null}
    >
      {read.failed && read.shown !== null && (
        <LoadError message={`Could not refresh — showing the campaigns as last read. ${read.error}`} />
      )}
      <DataTable
        label="Campaigns"
        columns={CAMPAIGN_COLUMNS}
        rows={read.tableState(['campaign', 'campaigns'], '') ? null : (read.shown?.page.items ?? null)}
        rowKey={(row) => row.id}
        rowProps={(row) => ({ 'data-campaign': row.id }) as React.HTMLAttributes<HTMLTableRowElement>}
        onOpen={(row) => setOpen(row.id)}
        busy={read.reading && read.shown !== null}
        state={read.tableState(['campaign', 'campaigns'], chips.length ? 'No campaign matches.' : 'No campaign yet — send one from Lists.')}
        minWidth="64rem"
        stickyTop={STICKY_TABLE_TOP}
      />
      {read.shown && <WorkPager {...read.paging.pager} total={read.shown.page.total} busy={read.reading} sticky />}
      <CampaignSheet campaignId={open} onClose={() => setOpen(null)} onChanged={() => void read.reload()} />
    </LogFrame>
  );
}

const CAMPAIGN_COLUMNS: IDataColumn<IMailCampaignSummary>[] = [
  { id: 'name', header: 'Campaign', className: 'font-medium', cell: (row) => row.name ?? row.templateName },
  {
    id: 'template',
    header: 'Template',
    cell: (row) => (
      <TemplateCell name={row.templateName} survey={row.survey} version={row.version ?? null} tags={row.tags} />
    )
  },
  { id: 'kind', header: 'Kind', cell: (row) => CAMPAIGN_KINDS[row.kind] ?? row.kind },
  { id: 'status', header: 'Status', cell: (row) => <CampaignStatusChip status={row.status} /> },
  { id: 'recipients', header: 'Recipients', numeric: true, cell: (row) => row.recipients.toLocaleString('en-US') },
  { id: 'sent', header: 'Sent', numeric: true, cell: (row) => row.counts.sent.toLocaleString('en-US') },
  { id: 'queued', header: 'Queued', numeric: true, cell: (row) => row.counts.queued.toLocaleString('en-US') },
  {
    id: 'failed',
    header: 'Failed / skipped',
    numeric: true,
    cell: (row) => `${row.counts.failed} / ${row.counts.skipped}`
  },
  { id: 'by', header: 'By', className: 'break-all', cell: (row) => row.createdBy ?? '' },
  {
    id: 'when',
    header: 'Launched',
    className: 'whitespace-nowrap text-xs text-muted-foreground',
    cell: (row) => (row.launchedAt ? formatDateTime(row.launchedAt) : 'not yet')
  },
  {
    id: 'open',
    header: '',
    label: 'Open',
    headClassName: 'w-10',
    cell: () => <ArrowRight className="size-4 text-muted-foreground" aria-hidden />
  }
];

// ---------------------------------------------------------------------------------------------
// Suppressions
// ---------------------------------------------------------------------------------------------

const SUPPRESSION_FILTERS: readonly IKnownFilter[] = [
  { param: 'source', column: 'source', label: 'Source', options: vocabulary(SUPPRESSION_SOURCES), labelOf: sourceLabel },
  {
    param: 'active',
    column: 'state',
    label: 'State',
    options: [
      { value: 'true', label: 'Active' },
      { value: 'false', label: 'Active and cleared' }
    ],
    labelOf: (value) => (value === 'false' ? 'Active and cleared' : 'Active')
  }
];

export function AdminMailSuppressionsPage() {
  const can = useCan();
  if (!can('mail.outreach.send')) return <Denied what="managing the suppression list" />;
  return <Suppressions />;
}

function Suppressions() {
  const filters = useFilters(['q', 'source', 'active']);
  const { params, q } = filters;
  const query = {
    q: q || undefined,
    source: params.get('source') || undefined,
    active: params.get('active') !== 'false'
  };
  const read = usePagedRead<IMailSuppression>(
    JSON.stringify(query),
    (page, pageSize, signal) => mailLogApi.suppressions({ ...query, page, pageSize }, signal),
    'The suppressions could not be read'
  );
  const funnels = useKnownFunnels(SUPPRESSION_FILTERS, params, filters.choose);
  const [adding, setAdding] = React.useState(false);
  const [clearing, setClearing] = React.useState<Nullable<IMailSuppression>>(null);
  const [said, setSaid] = React.useState<Nullable<string>>(null);
  const help = (
    <HelpPopover label="About suppressions" align="end">
      <p>
        Addresses bulk email skips — invitations, reminders, outreach. An address lands here when
        its owner unsubscribes, marks an email as spam, or staff add it. Emails about something the
        person did themselves still reach it.
      </p>
      <p>Clearing one means the person agreed to receive bulk email again; say how in the note.</p>
    </HelpPopover>
  );
  const add = (
    <PageAction label="Suppress an address" icon={<MailX className="size-4" aria-hidden />} onClick={() => setAdding(true)} />
  );
  return (
    <LogFrame
      summary={read.total === null ? null : countOf(read.total, 'address', 'addresses')}
      refresh={read.refresh}
      help={help}
      search={(compact) => (
        <RecordSearch
          q={q}
          label="Filter suppressions"
          placeholder="Address or name…"
          onSearch={(text) => filters.search(text, () => void read.reload())}
          compact={compact}
        />
      )}
      end={add}
      chips={funnels.chips}
      onClearAll={filters.clearAll}
      reading={read.reading}
      found={read.shown?.page.total ?? null}
    >
      {said && (
        <p role="status" className="text-sm">
          {said}
        </p>
      )}
      {read.failed && read.shown !== null && (
        <LoadError message={`Could not refresh — showing the suppressions as last read. ${read.error}`} />
      )}
      <DataTable
        label="Suppressions"
        columns={suppressionColumns(funnels.header, setClearing)}
        rows={read.tableState(['address', 'addresses'], '') ? null : (read.shown?.page.items ?? null)}
        rowKey={(row) => row.id}
        rowProps={(row) => ({ 'data-suppression': row.address }) as React.HTMLAttributes<HTMLTableRowElement>}
        busy={read.reading && read.shown !== null}
        state={read.tableState(['address', 'addresses'], funnels.chips.length || q ? 'No suppression matches.' : 'No address is suppressed.')}
        minWidth="64rem"
        stickyTop={STICKY_TABLE_TOP}
      />
      {read.shown && <WorkPager {...read.paging.pager} total={read.shown.page.total} busy={read.reading} sticky />}
      <SuppressDialog
        open={adding}
        onOpenChange={setAdding}
        onDone={(address) => {
          setSaid(`${address} is suppressed: bulk email skips it from now on.`);
          void read.reload();
        }}
      />
      <ClearSuppressionDialog
        suppression={clearing}
        onClose={() => setClearing(null)}
        onDone={(address) => {
          setSaid(`${address} receives bulk email again.`);
          void read.reload();
        }}
      />
    </LogFrame>
  );
}

function suppressionColumns(
  head: (filter: IKnownFilter) => React.ReactNode,
  onClear: (row: IMailSuppression) => void
): IDataColumn<IMailSuppression>[] {
  return [
    { id: 'address', header: 'Address', className: 'font-medium whitespace-nowrap', cell: (row) => row.address },
    { id: 'source', header: head(SUPPRESSION_FILTERS[0]), label: 'Source', cell: (row) => sourceLabel(row.source) },
    {
      id: 'participants',
      header: 'Participants',
      cell: (row) =>
        row.participants.length === 0 ? (
          <span className="text-muted-foreground">—</span>
        ) : (
          <span className="grid">
            {row.participants.map((participant) => (
              <Link key={participant.id} to={personHubPath(participant.id)} className="text-[var(--color-sapphire)] hover:underline">
                {participant.name}
              </Link>
            ))}
          </span>
        )
    },
    {
      id: 'added',
      header: 'Added',
      className: 'text-xs',
      cell: (row) => (
        <span className="grid">
          <span className="whitespace-nowrap text-muted-foreground">{formatDateTime(row.createdAt)}</span>
          {row.createdBy && <span className="break-all text-muted-foreground">{row.createdBy}</span>}
          {row.note && <span className="break-words">{row.note}</span>}
        </span>
      )
    },
    {
      id: 'state',
      header: head(SUPPRESSION_FILTERS[1]),
      label: 'State',
      className: 'text-xs',
      cell: (row) =>
        row.clearedAt ? (
          <span className="grid">
            <span className="whitespace-nowrap text-muted-foreground">Cleared {formatDateTime(row.clearedAt)}</span>
            {row.clearedBy && <span className="break-all text-muted-foreground">{row.clearedBy}</span>}
            {row.clearedNote && <span className="break-words">{row.clearedNote}</span>}
          </span>
        ) : (
          <Button type="button" variant="outline" size="sm" onClick={() => onClear(row)} data-clear-suppression={row.address}>
            Clear…
          </Button>
        )
    }
  ];
}

// ---------------------------------------------------------------------------------------------
// Unmatched replies
// ---------------------------------------------------------------------------------------------

export function AdminMailRepliesPage() {
  if (!useCanReadLog()) return <Denied what="reading the mail log" />;
  return <Replies />;
}

/** The replies no send matched — a reply key lost, or mail to the routed domain from elsewhere —
 *  waiting for someone to read them; `reviewed=1` lists the reviewed ones too. */
function Replies() {
  const filters = useFilters(['reviewed']);
  const { params, setParams } = filters;
  const withReviewed = params.get('reviewed') === '1';
  const query = { unmatched: true, reviewed: withReviewed ? undefined : false };
  const read = usePagedRead<IMailReply>(
    JSON.stringify(query),
    (page, pageSize, signal) => mailLogApi.replies({ ...query, page, pageSize }, signal),
    'The replies could not be read'
  );
  const [open, setOpen] = React.useState<Nullable<string>>(null);
  const toggle = () =>
    setParams(
      (now) => {
        const next = dropPaging(new URLSearchParams(now));
        if (withReviewed) next.delete('reviewed');
        else next.set('reviewed', '1');
        return next;
      },
      { replace: true }
    );
  const help = (
    <HelpPopover label="About unmatched replies" align="end">
      <p>
        Replies that came to the reply address but match no email sent — the reply code was lost, or
        someone wrote to the address directly. Nothing is done with them automatically: read each,
        act on it where it belongs, and mark it reviewed.
      </p>
    </HelpPopover>
  );
  const toggleButton = (
    <Button type="button" variant="ghost" size="sm" aria-pressed={withReviewed} onClick={toggle} data-show-reviewed="">
      {withReviewed ? 'Hide reviewed' : 'Show reviewed'}
    </Button>
  );
  return (
    <LogFrame
      summary={read.total === null ? null : countOf(read.total, withReviewed ? 'reply' : 'reply to review', withReviewed ? 'replies' : 'replies to review')}
      refresh={read.refresh}
      help={help}
      search={() => null}
      end={toggleButton}
      chips={[]}
      onClearAll={filters.clearAll}
      reading={read.reading}
      found={read.shown?.page.total ?? null}
    >
      {read.failed && read.shown !== null && (
        <LoadError message={`Could not refresh — showing the replies as last read. ${read.error}`} />
      )}
      <DataTable
        label="Unmatched replies"
        columns={replyColumns()}
        rows={read.tableState(['reply', 'replies'], '') ? null : (read.shown?.page.items ?? null)}
        rowKey={(row) => row.id}
        rowProps={(row) => ({ 'data-reply': row.id }) as React.HTMLAttributes<HTMLTableRowElement>}
        onOpen={(row) => setOpen(row.id)}
        busy={read.reading && read.shown !== null}
        state={read.tableState(['reply', 'replies'], withReviewed ? 'No unmatched reply yet.' : 'Nothing to review.')}
        minWidth="56rem"
        stickyTop={STICKY_TABLE_TOP}
      />
      {read.shown && <WorkPager {...read.paging.pager} total={read.shown.page.total} busy={read.reading} sticky />}
      <ReplyDrawer replyId={open} onClose={() => setOpen(null)} onReviewed={() => void read.reload()} />
    </LogFrame>
  );
}

function replyColumns(): IDataColumn<IMailReply>[] {
  return [
    {
      id: 'at',
      header: 'Received',
      className: 'whitespace-nowrap text-xs text-muted-foreground',
      cell: (row) => formatDateTime(row.receivedAt)
    },
    { id: 'from', header: 'From', className: 'break-all', cell: (row) => row.from },
    { id: 'subject', header: 'Subject', className: 'break-words', cell: (row) => row.subject || '(no subject)' },
    {
      id: 'files',
      header: 'Files',
      numeric: true,
      cell: (row) =>
        row.attachments.length > 0 ? (
          <span className="inline-flex items-center gap-1">
            <Paperclip className="size-3.5 text-muted-foreground" aria-hidden />
            {row.attachments.length}
          </span>
        ) : (
          <span className="text-muted-foreground">—</span>
        )
    },
    {
      id: 'reviewed',
      header: 'Reviewed',
      className: 'text-xs',
      cell: (row) =>
        row.reviewedAt ? (
          <span className="grid text-muted-foreground">
            <span className="whitespace-nowrap">{formatDateTime(row.reviewedAt)}</span>
            {row.reviewedBy && <span className="break-all">{row.reviewedBy}</span>}
          </span>
        ) : (
          <StatusChip tone="attention">To review</StatusChip>
        )
    },
    {
      id: 'open',
      header: '',
      label: 'Open',
      headClassName: 'w-10',
      cell: () => <ArrowRight className="size-4 text-muted-foreground" aria-hidden />
    }
  ];
}
